import { StepFlowShell } from '@/components/step-flow/StepFlowShell';
import type { StepFlowLang } from '@/components/step-flow/StepFlowShell.types';
import { useAuth } from '@/contexts/AuthContext';
import { OnboardingIllustration } from '@/pages/onboarding/OnboardingIllustration';
import { OnboardingStepBody } from '@/pages/onboarding/OnboardingStepBody';
import type { OnboardingFlowProps, OnboardingFormValues } from '@/pages/onboarding/onboarding.types';
import { ONBOARDING_TOTAL_STEPS, onboardingContent } from '@/pages/onboarding/onboardingContent';
import { isStepValid, toOnboardingProfile } from '@/pages/onboarding/onboardingForm';
import { suggestedSignature } from '@/pages/onboarding/onboardingSummary';
import { useCompleteOnboarding } from '@/services/onboarding/useCompleteOnboarding';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

export const OnboardingFlow = ({ initialValues, isEditMode, isFirstRun }: OnboardingFlowProps) => {
  const { user } = useAuth();
  const [lang, setLang] = useState<StepFlowLang>('is');
  const [step, setStep] = useState(1);
  const copy = onboardingContent[lang];
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? '';

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { dirtyFields },
  } = useForm<OnboardingFormValues>({
    defaultValues: initialValues,
  });
  const values = watch();

  // A first run with no saved signature gets one from the answers ("Nafn, starfsheiti"),
  // kept in step with them until the person types their own.
  const [hasOwnSignature, setHasOwnSignature] = useState(isEditMode || initialValues.letterSignature.trim() !== '');
  const signatureSuggestion = suggestedSignature(values, copy, user?.name);
  useEffect(() => {
    if (hasOwnSignature || values.letterSignature === signatureSuggestion) return;
    setValue('letterSignature', signatureSuggestion, { shouldDirty: true });
  }, [hasOwnSignature, signatureSuggestion, values.letterSignature, setValue]);
  const completion = useCompleteOnboarding(copy.toast, { isFirstRun });

  const isLastStep = step === ONBOARDING_TOTAL_STEPS;
  const nextDisabled = !isStepValid(step, values) || (isLastStep && completion.isSubmitted);
  const titles = [
    copy.profession.greeting(firstName),
    copy.work.title,
    copy.detail.title,
    copy.summary.greeting(firstName),
  ];
  const subtitles = [copy.profession.subtitle, copy.work.subtitle, copy.detail.subtitle, copy.summary.subtitle];

  const submit = handleSubmit((formValues) =>
    completion.submit(toOnboardingProfile(formValues, !!dirtyFields.letterSignature))
  );

  const handleNext = () => {
    if (isLastStep) {
      submit();
      return;
    }
    setStep((current) => current + 1);
  };

  return (
    <StepFlowShell
      step={step}
      totalSteps={ONBOARDING_TOTAL_STEPS}
      lang={lang}
      onLangChange={setLang}
      stepLabel={copy.stepLabel(step, ONBOARDING_TOTAL_STEPS)}
      title={titles[step - 1]}
      subtitle={subtitles[step - 1]}
      illustration={<OnboardingIllustration step={step} values={values} copy={copy} />}
      onBack={step > 1 ? () => setStep((current) => current - 1) : undefined}
      backLabel={copy.nav.back}
      onNext={handleNext}
      nextLabel={isLastStep ? (completion.isPending ? copy.nav.submitting : copy.nav.complete) : copy.nav.next}
      nextDisabled={nextDisabled}
      nextLoading={completion.isPending}
    >
      <OnboardingStepBody
        step={step}
        copy={copy}
        control={control}
        values={values}
        onEditStep={setStep}
        isEditMode={isEditMode}
        onSignatureEdit={() => setHasOwnSignature(true)}
      />
    </StepFlowShell>
  );
};
